import { QueryKeys } from "@app/constants/query-keys.ts";
import { useApis } from "@app/data/apis.ts";
import {
    InsertWorkPackagePostModel,
    UpdateWorkPackageActivationPostModel,
    UpdateWorkPackagePostModel,
    UpdateWorkPackageSortOrderPostModel,
} from "@app/data/engine.ts";
import { handleApiError } from "@app/helpers/error-helper.tsx";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useQuery } from "bi-lib";
import { toast } from "react-toastify";

interface Props {
    assignmentId?: number;
    phaseId?: number;
    packageId?: number;
    activeOnly?: boolean;
}

export default function useWorkPackageData({ assignmentId, phaseId, packageId, activeOnly }: Props) {
    const apis = useApis();
    const queryClient = useQueryClient();

    const workPackagesListByAssignmentQuery = useQuery({
        queryKey: [QueryKeys.WorkPackageListByAssignment, { assignmentId, phaseId }],
        queryFn: () =>
            apis.engine.workPackage.workPackageFilterWorkPackagesListList({
                assignmentId,
                phaseId,
                activeOnly,
            }),
    });

    const workPackageQuery = useQuery({
        queryKey: [QueryKeys.WorkPackage, { packageId }],
        queryFn: () =>
            apis.engine.workPackage.workPackageGetAssignmentPackageList({
                id: packageId,
            }),
    });

    const filterWorkPackagesListQuery = useQuery({
        queryKey: [QueryKeys.FilterWorkPackagesList, { assignmentId, phaseId, activeOnly }],
        queryFn: () =>
            apis.engine.workPackage.workPackageFilterWorkPackagesListList({ assignmentId, phaseId, activeOnly }),
    });

    const createWorkPackageMutation = useMutation({
        mutationFn: (post: InsertWorkPackagePostModel) =>
            apis.engine.workPackage.workPackageCreateAssignmentWorkPackageCreate(post),
        onSuccess: () => {
            void queryClient.invalidateQueries({
                queryKey: [QueryKeys.WorkPackageListByAssignment],
            });
            void queryClient.invalidateQueries({
                queryKey: [QueryKeys.FilterWorkPackagesList],
            });
            toast.success("Work package saved");
        },
        onError: (error: Error) => handleApiError(error, "Failed to save work package"),
    });

    const updateWorkPackageMutation = useMutation({
        mutationFn: (post: UpdateWorkPackagePostModel) =>
            apis.engine.workPackage.workPackageCreateAssignmentWorkPackageCreate(post),
        onSuccess: (_, { workPackageId }) => {
            void queryClient.invalidateQueries({
                queryKey: [QueryKeys.WorkPackageListByAssignment],
            });
            void queryClient.invalidateQueries({
                queryKey: [QueryKeys.WorkPackage, { packageId: workPackageId }],
            });
            void queryClient.invalidateQueries({
                queryKey: [QueryKeys.FilterWorkPackagesList],
            });
            toast.success("Work package saved");
        },
        onError: (error: Error) => handleApiError(error, "Failed to save work package"),
    });

    const updateWorkPackageActivationMutation = useMutation({
        mutationFn: (post: UpdateWorkPackageActivationPostModel) =>
            apis.engine.workPackage.workPackageUpdateWorkPackageActivationCreate(post),
        onSuccess: (_, { workPackageId }) => {
            void queryClient.invalidateQueries({
                queryKey: [QueryKeys.WorkPackageListByAssignment],
            });
            void queryClient.invalidateQueries({
                queryKey: [QueryKeys.WorkPackage, { id: workPackageId }],
            });
            void queryClient.invalidateQueries({
                queryKey: [QueryKeys.FilterWorkPackagesList],
            });
            toast.success("Work package saved");
        },
        onError: (error: Error) => handleApiError(error, "Failed to save work package"),
    });

    const updateWorkPackageSortByMutation = useMutation({
        mutationFn: (post: UpdateWorkPackageSortOrderPostModel) =>
            apis.engine.workPackage.workPackageUpdateWorkPackageSortByCreate(post),
        onSuccess: (_, { packageId }) => {
            void queryClient.invalidateQueries({
                queryKey: [QueryKeys.WorkPackageListByAssignment],
            });
            void queryClient.invalidateQueries({
                queryKey: [QueryKeys.WorkPackage, { id: packageId }],
            });
            toast.success("Assignment phase saved");
        },
        onError: (error: Error) => handleApiError(error, "Failed to save assignment phase"),
    });

    return {
        workPackagesListByAssignment: workPackagesListByAssignmentQuery,
        workPackage: workPackageQuery,
        filterWorkPackagesList: filterWorkPackagesListQuery,

        createWorkPackage: createWorkPackageMutation,
        updateWorkPackage: updateWorkPackageMutation,
        updateWorkPackageActivation: updateWorkPackageActivationMutation,
        updateWorkPackageSortBy: updateWorkPackageSortByMutation,
    };
}
